import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { consulta, pedir } from '../api/nucleo'
import { Aviso, Boton, Tarjeta } from '../componentes/ui'
import { CampoGenerico } from '../motor/Campo'
import { campoPorNombre } from '../motor/esquema'
import { useFormulario } from '../motor/Formulario'

/**
 * La pestaña de SEO de un documento (CLAUDE.md §10, §20): los campos del grupo `seo`,
 * el análisis determinista con su semáforo y sus comprobaciones —que no cuestan nada—,
 * y el asistente, que **propone** y no aplica: cada sugerencia se acepta o se descarta
 * a mano, y antes de pedirla se enseña lo que cuesta.
 */
type Analisis = {
  puntuacion: number
  semaforo: 'rojo' | 'ambar' | 'verde'
  comprobaciones: Array<{ id: string; titulo: string; grupo: string; peso: number; estado: 'bien' | 'mal' | 'mejorable' | 'no-aplica'; mensaje: string }>
}
type Estimacion = { creditos: number; saldo: number; modelo: string }
type Sugerencias = {
  sugerencias: { titulos?: string[]; descripciones?: string[]; palabraClave?: string; palabrasSecundarias?: string[]; faltaInformacion?: string }
  creditos: { consumidos: number; saldo: number }
}

const COLOR: Record<string, string> = { verde: 'bg-ok-suave text-ok', ambar: 'bg-aviso-suave text-aviso', rojo: 'bg-error-suave text-error' }
const ESTADO: Record<string, string> = { bien: 'text-ok', mal: 'text-error', mejorable: 'text-aviso', 'no-aplica': 'text-tenue' }
const ICONO: Record<string, string> = { bien: '✓', mal: '✕', mejorable: '!', 'no-aplica': '–' }

export const PestanaSeo = ({ coleccion, id }: { coleccion: string; id: null | number }) => {
  const { esquema, sitio, cambiar, valores } = useFormulario()
  const grupo = campoPorNombre(esquema.campos, 'seo')
  const { data: analisis, refetch } = useQuery({
    queryKey: ['seo-analisis', coleccion, id],
    enabled: id !== null && sitio !== null,
    queryFn: () => pedir<Analisis>(`/seo/analisis${consulta({ sitio: sitio ?? undefined, coleccion, id: id ?? undefined })}`),
  })
  const [estimacion, setEstimacion] = useState<Estimacion | null>(null)
  const [propuesta, setPropuesta] = useState<Sugerencias | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<null | string>(null)

  const pedirSugerencias = async (confirmar: boolean) => {
    if (id === null || sitio === null) return
    setOcupado(true)
    setError(null)
    try {
      if (!confirmar) {
        setEstimacion(await pedir<Estimacion>('/seo/sugerir/estimar', { metodo: 'POST', cuerpo: { sitio, clase: 'titulo-y-descripcion', coleccion, documento: id } }))
      } else {
        setPropuesta(await pedir<Sugerencias>('/seo/sugerir', { metodo: 'POST', cuerpo: { sitio, clase: 'titulo-y-descripcion', coleccion, documento: id } }))
        setEstimacion(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se ha podido pedir la sugerencia.')
    } finally {
      setOcupado(false)
    }
  }

  const seo = (valores.seo ?? {}) as Record<string, unknown>

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="flex flex-col gap-5">
        {/* Cómo se vería en Google, con lo que hay escrito ahora. */}
        <Tarjeta>
          <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">Así se vería en un buscador</p>
          <p className="truncate text-lg text-[#1a0dab] dark:text-[#8ab4f8]">{String(seo.titulo || valores[esquema.titulo] || 'Sin título')}</p>
          <p className="mt-1 line-clamp-2 text-sm text-tinta-2">{String(seo.descripcion || 'Sin descripción: los buscadores enseñarán un trozo del texto que ellos elijan.')}</p>
        </Tarjeta>
        {grupo && <CampoGenerico campo={{ ...grupo, etiqueta: 'Ajustes de SEO' }} ruta="seo" />}
      </div>

      <aside className="flex flex-col gap-4">
        {id === null ? (
          <Aviso>Guarda el documento para ver su análisis.</Aviso>
        ) : analisis ? (
          <Tarjeta>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium">Puntuación</span>
              <span className={`rounded-boton px-3 py-1 text-sm font-semibold ${COLOR[analisis.semaforo] ?? ''}`}>{analisis.puntuacion} / 100</span>
            </div>
            <ul className="flex flex-col gap-2 text-sm">
              {analisis.comprobaciones
                .filter((c) => c.estado !== 'no-aplica')
                .sort((a, b) => (a.estado === 'mal' ? -1 : b.estado === 'mal' ? 1 : a.estado === 'mejorable' ? -1 : 1))
                .map((c) => (
                  <li className="flex gap-2" key={c.id}>
                    <span className={`w-4 shrink-0 font-bold ${ESTADO[c.estado] ?? ''}`}>{ICONO[c.estado]}</span>
                    <span>
                      <span className="font-medium">{c.titulo}</span>
                      {c.estado !== 'bien' && <span className="block text-tinta-2">{c.mensaje}</span>}
                    </span>
                  </li>
                ))}
            </ul>
            <p className="mt-3 text-xs text-tenue">Se recalcula al guardar. Es determinista: la misma página da la misma nota hoy y mañana.</p>
            <Boton className="mt-2" onClick={() => void refetch()} pequeno tipo="discreto">
              Volver a mirar
            </Boton>
          </Tarjeta>
        ) : null}

        {id !== null && (
          <Tarjeta>
            <p className="text-sm font-medium">Asistente</p>
            <p className="mt-1 text-xs text-tenue">Propone título y descripción a partir de lo que hay escrito. Tú eliges; nada se aplica solo.</p>
            {error && (
              <div className="mt-2">
                <Aviso tono="error">{error}</Aviso>
              </div>
            )}
            {!estimacion && !propuesta && (
              <Boton className="mt-3" disabled={ocupado} onClick={() => void pedirSugerencias(false)} pequeno>
                {ocupado ? 'Calculando…' : 'Proponer título y descripción'}
              </Boton>
            )}
            {estimacion && (
              <div className="mt-3 text-sm">
                <p>
                  Cuesta unos <strong>{estimacion.creditos}</strong> créditos (quedan {estimacion.saldo}).
                </p>
                <div className="mt-2 flex gap-2">
                  <Boton disabled={ocupado || estimacion.saldo < estimacion.creditos} onClick={() => void pedirSugerencias(true)} pequeno tipo="primario">
                    {ocupado ? 'Pidiendo…' : 'Pedir'}
                  </Boton>
                  <Boton onClick={() => setEstimacion(null)} pequeno tipo="discreto">
                    Cancelar
                  </Boton>
                </div>
              </div>
            )}
            {propuesta && (
              <div className="mt-3 flex flex-col gap-3 text-sm">
                {propuesta.sugerencias.faltaInformacion && <Aviso>{propuesta.sugerencias.faltaInformacion}</Aviso>}
                {(propuesta.sugerencias.titulos ?? []).length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-tenue uppercase">Títulos</p>
                    {propuesta.sugerencias.titulos!.map((t) => (
                      <button className="mb-1 block w-full rounded-lg border border-linea px-3 py-2 text-left hover:border-violeta" key={t} onClick={() => cambiar('seo.titulo', t)} type="button">
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                {(propuesta.sugerencias.descripciones ?? []).length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-tenue uppercase">Descripciones</p>
                    {propuesta.sugerencias.descripciones!.map((d) => (
                      <button className="mb-1 block w-full rounded-lg border border-linea px-3 py-2 text-left hover:border-violeta" key={d} onClick={() => cambiar('seo.descripcion', d)} type="button">
                        {d}
                      </button>
                    ))}
                  </div>
                )}
                {propuesta.sugerencias.palabraClave && (
                  <button className="rounded-lg border border-linea px-3 py-2 text-left hover:border-violeta" onClick={() => cambiar('seo.palabraClave', propuesta.sugerencias.palabraClave)} type="button">
                    Palabra clave: <strong>{propuesta.sugerencias.palabraClave}</strong>
                  </button>
                )}
                <p className="text-xs text-tenue">Pulsa una para ponerla en el campo; después guarda. {propuesta.creditos.consumidos} créditos.</p>
                <Boton onClick={() => setPropuesta(null)} pequeno tipo="discreto">
                  Cerrar
                </Boton>
              </div>
            )}
          </Tarjeta>
        )}
      </aside>
    </div>
  )
}
